// Left pane: Open / Mine / Unassigned / Closed with counts, search, and an endless list.

import { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api, type ConversationSummary } from '../../api';
import { Loading, ErrorBox, Empty } from '../../components/States';
import { displayName, fmtRelative, initials } from '../../lib/format';

export type Box = 'open' | 'mine' | 'unassigned' | 'closed';

const BOXES: { id: Box; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'mine', label: 'Mine' },
  { id: 'unassigned', label: 'Unassigned' },
  { id: 'closed', label: 'Closed' },
];

export function ConversationList({
  box,
  setBox,
  q,
  setQ,
  activeId,
  onSelect,
}: {
  box: Box;
  setBox: (b: Box) => void;
  q: string;
  setQ: (s: string) => void;
  activeId: string | null;
  onSelect: (id: string) => void;
}) {
  const [debounced, setDebounced] = useState(q);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q.trim()), 250);
    return () => window.clearTimeout(t);
  }, [q]);

  const counts = useQuery({ queryKey: ['counts'], queryFn: api.conversations.counts, staleTime: 5_000 });

  const list = useInfiniteQuery({
    queryKey: ['conversations', box, debounced],
    queryFn: ({ pageParam }) => api.conversations.list({ box, q: debounced || undefined, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  // load more when the sentinel at the bottom scrolls into view
  const sentinel = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && list.hasNextPage && !list.isFetchingNextPage) list.fetchNextPage();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [list.hasNextPage, list.isFetchingNextPage, list.fetchNextPage, list]);

  const items = list.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <>
      <div className="boxes" role="tablist">
        {BOXES.map((b) => (
          <button key={b.id} role="tab" aria-selected={box === b.id} className={box === b.id ? 'active' : ''} onClick={() => setBox(b.id)}>
            {b.label}
            {counts.data && <span className="count">{counts.data[b.id]}</span>}
          </button>
        ))}
      </div>
      <div className="search">
        <input id="global-search" className="input" placeholder="Search name, number, or text  (Ctrl+K)" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="pane-body">
        {list.isPending && <Loading />}
        {list.isError && <ErrorBox error={list.error} retry={() => list.refetch()} />}
        {list.isSuccess && items.length === 0 && (
          <Empty title={debounced ? 'No matches' : emptyTitle(box)} hint={debounced ? 'Try a different name or number.' : emptyHint(box)} />
        )}
        {items.map((c) => (
          <Row key={c.id} c={c} active={c.id === activeId} onClick={() => onSelect(c.id)} />
        ))}
        <div ref={sentinel} />
        {list.isFetchingNextPage && <Loading label="Loading more" />}
      </div>
    </>
  );
}

function emptyTitle(box: Box): string {
  switch (box) {
    case 'open': return 'No open conversations';
    case 'mine': return 'Nothing assigned to you';
    case 'unassigned': return 'Nothing unassigned';
    case 'closed': return 'No closed conversations';
  }
}
function emptyHint(box: Box): string {
  switch (box) {
    case 'open': return 'New texts land here. Use New text to start one.';
    case 'mine': return 'Conversations handed to you show up here.';
    case 'unassigned': return 'Every new text has been picked up.';
    case 'closed': return 'Closed conversations reopen on their own when the customer writes back.';
  }
}

function Row({ c, active, onClick }: { c: ConversationSummary; active: boolean; onClick: () => void }) {
  const last = c.last_message;
  const glyph = last ? (last.direction === 'in' ? '←' : '→') : '';
  const hasPicture = !!last?.attachments?.length;
  const preview = last ? `${hasPicture ? '[picture] ' : ''}${last.body.trim() || (!hasPicture && last.direction === 'in' ? '[picture]' : '')}`.trim() : 'No messages yet';
  const isNew = !c.contact.name;
  return (
    <div className={`convo-row ${active ? 'active' : ''}`} onClick={onClick} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onClick()}>
      <div>{c.unread_for_me ? <div className="unread" title="Unread" /> : null}</div>
      <div style={{ minWidth: 0 }}>
        <div className="who">
          <span className="name">{displayName(c.contact)}</span>
          {isNew && <span className="chip accent">New</span>}
          {c.contact.opted_out && <span className="chip danger">OPTED OUT</span>}
          {!c.contact.opted_out && c.opt_out_suspected && <span className="chip warn">may have opted out</span>}
        </div>
        <div className="preview">
          <span className="faint">{glyph} </span>
          {preview}
        </div>
      </div>
      <div className="meta">
        <span>{fmtRelative(c.last_message_at)}</span>
        {c.assigned_user && <span className="avatar" title={`Assigned to ${c.assigned_user.name}`}>{initials(c.assigned_user.name)}</span>}
      </div>
    </div>
  );
}
